import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { addErrorNotification, addSuccessNotification, NotificationType, updateMemberPhoto } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { useContext, useState } from 'react';
import { useI18n } from '../../../../context/occ/hooks/use-i18n';
import { FileInput, FileInputFileType, FileInputMode } from '../../../../subcomponents/form/input/file/file-input';
import { isValidFileType } from '../../../../subcomponents/form/input/file/utils/is-valid-file-type';
import { getIdForComponentNotification } from '../../../../utils/get-id-for-component-notification';
import { getImageDimensions } from '../../../../utils/images/get-image-dimensions';
import { MemberPhotoUploaderContextInstance } from '../types/context';
import { MemberPhotoUploaderEditPhotoModal } from './edit-photo-modal';
const b = bemClassBuilder('v-member-photo-uploader-file-form');
export const MemberPhotoUploaderFileForm = observer(props => {
    const i18n = useI18n();
    const { onPhotoUploaded, photoRequirements } = props;
    const { displayText, assets } = useContext(MemberPhotoUploaderContextInstance);
    const [imageString, setImageString] = useState();
    const handleEditModalClosed = () => {
        setImageString(undefined);
    };
    const handleFilesChanged = async (files) => {
        if (files.length > 1) {
            return validationErrorNotification(displayText.notificationInvalidFileCountMessage);
        }
        const imageFile = files[0];
        if (!isValidFileType(imageFile.type)) {
            return validationErrorNotification(displayText.notificationInvalidFileTypeMessage);
        }
        const newImageString = URL.createObjectURL(imageFile);
        try {
            const imageDimensions = await getImageDimensions(newImageString);
            if (isImageTooSmall(photoRequirements.dimensions, imageDimensions)) {
                const message = i18n.template(displayText.notificationInvalidImageDimensionsMessage, photoRequirements.dimensions);
                return validationErrorNotification(message);
            }
        }
        catch (_a) {
            return validationErrorNotification(displayText.notificationImageLoadFailedMessage);
        }
        setImageString(newImageString);
    };
    const handleSubmit = async (imageBlob) => {
        const formData = new FormData();
        formData.append('MemberPhoto', imageBlob, 'image.png');
        try {
            await updateMemberPhoto(formData);
            validationSuccessNotification(displayText.notificationImageUploadSuccessfulMessage);
            onPhotoUploaded();
        }
        catch (_a) {
            validationErrorNotification(displayText.notificationImageUploadFailedMessage);
        }
    };
    return (_jsxs("div", Object.assign({ className: b.block({ hasImage: !!imageString }) }, { children: [_jsx(FileInput, { mode: FileInputMode.Dropzone, onFilesChanged: handleFilesChanged, fileType: FileInputFileType.Image, browseButtonLabel: displayText.buttonBrowseFilesLabel, dropzonePromptMessage: displayText.fileDropzonePromptMessage, dropzonePromptIconName: assets.fileDropzonePromptIconName, dropzoneActiveDragIconName: assets.fileDropzoneOverlayIconName, multiple: false }), _jsx(FileInput, { mode: FileInputMode.Button, onFilesChanged: handleFilesChanged, fileType: FileInputFileType.Image, multiple: false, browseButtonLabel: displayText.buttonBrowseFilesLabel }), _jsx(MemberPhotoUploaderEditPhotoModal, { imageString: imageString, onFilesChanged: handleFilesChanged, onClose: handleEditModalClosed, onSubmit: handleSubmit, photoRequirements: props.photoRequirements })] })));
});
MemberPhotoUploaderFileForm.displayName = 'MemberPhotoUploaderFileForm';
const validationErrorNotification = (message) => {
    addErrorNotification({
        id: getIdForComponentNotification('member-photo-uploader', NotificationType.Error, 'submit'),
        messages: [message]
    });
};
const validationSuccessNotification = (message) => {
    addSuccessNotification({
        id: getIdForComponentNotification('member-photo-uploader', NotificationType.Success, 'submit'),
        messages: [message]
    });
};
const isImageTooSmall = (minimumDimensions, imageDimensions) => {
    return imageDimensions.width < minimumDimensions.width || imageDimensions.height < minimumDimensions.height;
};
